import classNames from 'classnames';
import { useState, useMemo } from 'react';
import ReactCollapsible from 'react-collapsible';
import { Button } from 'palette/Button';
import { BodyText, Header } from 'palette/Typography';
import { ReactOnRailsRootComponent } from '@common/ReactOnRails';
import { useTranslate } from '@common/Translations';
import '../styles/ListingDescription.scss';

const transitionTime = 100;

function TriggerButton(text) {
    return (
        <Button
            label={text}
            size="small"
            theme="secondary"
        />
    );
}

export function ListingDescription({
    customClass,
    description,
    descriptionId,
    isDescriptionExpanded = false,
    theme = 'white'
}) {
    const translate = useTranslate();
    const readMoreText = translate('read_more');
    const readLessText = translate('helper.view_less.label');
    const [buttonText, setButtonText] = useState(isDescriptionExpanded ? readLessText : readMoreText);
    const cutOffLimit = theme === 'white' ? 400 : 900;
    const isPreformatted = description.includes('</');
    const deepLinks = useMemo(() => {
        if (isPreformatted && description.indexOf('application/json') > -1) {
            try {
                return JSON.parse(description.match(/<script type="application\/json">(.*)<\/script>/)[1]);
            } catch {}
        }

        return null;
    }, [description, isPreformatted]);

    const descriptionContent = () => {
        let content = (
            <BodyText
                className="listing-description__wrapper"
                tagName="p"
            >
                {description}
            </BodyText>
        );

        /* eslint-disable react/no-danger */
        if (isPreformatted) {
            content = (
                <div
                    className="listing-description__wrapper body"
                    dangerouslySetInnerHTML={{ __html: description }}
                />
            );
        }

        return (
            <>
                {content}
                {deepLinks && (
                    <div className="listing-description__related-links">
                        {deepLinks.map(({ title, items }) => (
                            <div
                                key={title}
                                className="listing-description__related-links-block"
                            >
                                <Header
                                    className="listing-description__related-links-block-title"
                                    tagName="h3"
                                >
                                    {title}
                                </Header>
                                {items.map(({ name, url }) => (
                                    <a
                                        key={name}
                                        className="listing-description__related-link"
                                        href={url}
                                    >
                                        {name}
                                    </a>
                                ))}
                            </div>
                        ))}
                    </div>
                )}
            </>
        );
    };

    if (description && description.length > cutOffLimit) {
        return (
            <ReactCollapsible
                className={classNames(
                    customClass,
                    `listing-description--${theme}`
                )}
                classParentString="listing-description"
                contentElementId={descriptionId ?? `${descriptionId}_content`}
                open={isDescriptionExpanded}
                openedClassName={classNames(
                    customClass,
                    `listing-description--${theme}`,
                    'listing-description--active'
                )}
                transitionTime={transitionTime}
                trigger={TriggerButton(buttonText)}
                triggerElementProps={{ id: descriptionId }}
                onClose={() => setButtonText(readMoreText)}
                onOpen={() => setButtonText(readLessText)}
            >
                {descriptionContent()}
            </ReactCollapsible>
        );
    }

    return (
        <div
            className={classNames(
                customClass,
                'listing-description',
                `listing-description--${theme}`
            )}
        >
            {descriptionContent()}
        </div>
    );
}

export const ListingDescriptionStandalone = ReactOnRailsRootComponent(ListingDescription, { withReduxStore: true });
